import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Cookie } from "lucide-react";
import { PolicyDialog } from "@/components/PolicyDialog";
import { COOKIE_POLICY } from "@/data/legal";

const STORAGE_KEY = "bobos-cookie-consent";
export const COOKIE_CONSENT_EVENT = "bobos:open-cookie-consent";
export const openCookieSettings = () =>
  window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT));

export const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showPolicy, setShowPolicy] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) {
        const t = setTimeout(() => setVisible(true), 900);
        return () => clearTimeout(t);
      }
    } catch (e) {
      console.warn("Cookie consent: localStorage unavailable", e);
    }
  }, []);

  useEffect(() => {
    const open = () => setVisible(true);
    window.addEventListener(COOKIE_CONSENT_EVENT, open);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, open);
  }, []);

  const choose = (choice) => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ choice, at: new Date().toISOString() })
      );
    } catch (e) {
      console.warn("Cookie consent: failed to save preference", e);
    }
    setVisible(false);
  };

  return (
    <>
      <AnimatePresence>
        {visible && (
          <motion.div
            data-testid="cookie-consent-banner"
            initial={{ y: 140, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 140, opacity: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 26 }}
            className="fixed inset-x-0 bottom-0 z-[60] px-4 pb-4 sm:px-6 sm:pb-6"
          >
            <div className="mx-auto max-w-3xl rounded-3xl border-2 border-[#111111] bg-[#f7f0eb] shadow-2xl p-5 sm:p-6 flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#ff648b] text-[#111111]">
                  <Cookie className="h-6 w-6" />
                </span>
                <div>
                  <h3 className="font-display font-bold text-lg text-[#111111]">
                    We bake with cookies too
                  </h3>
                  <p className="text-sm text-[#3d3d3d] leading-relaxed mt-1">
                    We use cookies to keep this site sweet — remembering your
                    choices and helping us improve. Pick what works best for you,
                    or{" "}
                    <button
                      data-testid="cookie-banner-policy-link"
                      onClick={() => setShowPolicy(true)}
                      className="font-semibold underline text-[#111111] hover:text-[#ed4a76] transition-colors"
                    >
                      read our Cookie Policy
                    </button>
                    .
                  </p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  data-testid="cookie-accept-all"
                  onClick={() => choose("all")}
                  className="px-6 py-2.5 rounded-full bg-[#ff648b] text-[#111111] font-bold hover:bg-[#ed4a76] hover:scale-105 transition-all shadow-md"
                >
                  Allow cookies
                </button>
                <button
                  data-testid="cookie-accept-none"
                  onClick={() => choose("none")}
                  className="px-6 py-2.5 rounded-full bg-transparent border-2 border-[#111111]/20 text-[#3d3d3d] font-bold hover:border-[#111111]/45 hover:text-[#111111] transition-all"
                >
                  No cookies, just donuts
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <PolicyDialog
        open={showPolicy}
        onOpenChange={setShowPolicy}
        policy={COOKIE_POLICY}
      />
    </>
  );
};
